import React from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { LineChart } from '@mui/x-charts/LineChart';
import ShowChartOutlinedIcon from '@mui/icons-material/ShowChartOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import useApi from '../../hooks/useApi';
import { getFilteredSnapshotEnrollmentCount } from './wipHomeLandingPageUtils';

const currency = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
const integer = new Intl.NumberFormat('en-US');

const marketShareInputs = [
  { program: 'Website', lookupNames: ['Website'], enrolledBacs: 1071, sharePct: 26 },
  { program: 'Chat', lookupNames: ['Chat'], enrolledBacs: 56, sharePct: 3 },
  { program: 'Trade', lookupNames: ['Trade'], enrolledBacs: 106, sharePct: 5 },
  { program: 'SEO', lookupNames: ['SEO'], enrolledBacs: 241, sharePct: 20 },
  { program: 'DigAd Sales', lookupNames: ['Digital Ad Sales', 'DigAd Sales'], enrolledBacs: 230, sharePct: 12 },
  { program: 'DigAd Service', lookupNames: ['Digital Ad Service', 'DigAd Service'], enrolledBacs: 63, sharePct: 12 },
];

const HERO_TAGLINE = 'managing the gm program';

const ACTIVE_ENROLLMENT_STATUSES = new Set(['ENROLLED', 'ACTION_REQUIRED', 'IN_PROGRESS', 'PENDING_OPT_OUT']);

const deltaWindowOptions = [
  { key: '30d', label: '30D', days: 30 },
  { key: '3m', label: '3M', days: 90 },
  { key: '6m', label: '6M', days: 180 },
  { key: '1y', label: '1Yr', days: 365 },
];

const parseNumber = (value) => {
  if (value === null || value === undefined || value === '') return 0;
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  const normalized = String(value).replace(/[^0-9.-]/g, '');
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : 0;
};

const resolveEnrollmentDate = (row) => {
  const candidates = [
    row?.enrolledDt,
    row?.enrollmentDate,
    row?.enrollmentDt,
    row?.enrolledDate,
    row?.enrollmedDate,
    row?._original?.enrolledDt,
    row?._original?.enrollmentDate,
  ];
  for (const candidate of candidates) {
    if (!candidate) continue;
    const parsed = new Date(candidate);
    if (!Number.isNaN(parsed.getTime())) return parsed;
  }
  return null;
};

const resolveEnrollmentStatusCode = (row) => {
  const raw =
    row?.enrollmentStatusCd ||
    row?.enrollmentStatus ||
    row?._original?.enrollmentStatusCd ||
    row?._original?.enrollmentStatus ||
    '';
  return String(raw).trim().toUpperCase();
};

const resolveSelectionKey = (selection) => {
  return String(
    selection?.referenceId ||
      selection?.referenceID ||
      selection?.id ||
      `${selection?.serviceItemName || ''}:${selection?.vendorCode || ''}:${selection?.productBrandCode || ''}`
  );
};

const getMonthlyMrr = (selection) => {
  const amount = parseNumber(selection?.dealerPrice || selection?.price || selection?.monthlyAmount || 0);
  if (!amount) return 0;
  const frequency = String(selection?.billingFrequency || selection?.occurrence || '').toLowerCase();
  if (frequency.includes('year') || frequency.includes('annual')) return amount / 12;
  if (frequency.includes('quarter')) return amount / 3;
  if (frequency.includes('week')) return amount * 4.345;
  return amount;
};

const buildMonthBuckets = (months) => {
  const now = new Date();
  const labels = [];
  const keys = [];
  for (let i = months - 1; i >= 0; i -= 1) {
    const date = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i, 1));
    keys.push(`${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}`);
    labels.push(date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' }));
  }
  return { keys, labels };
};

const getSnapshotTimestamp = (snapshot) => {
  const raw = snapshot?.createdAt || snapshot?.updatedAt || snapshot?.date;
  if (!raw) return null;
  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const coerceEnrollmentCount = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = typeof value === 'number' ? value : Number(String(value).trim());
  return Number.isFinite(parsed) ? parsed : null;
};

const buildProgramSnapshotPoints = (snapshots = [], programName) => {
  const daily = new Map();
  snapshots.forEach((snapshot) => {
    if (String(snapshot?.programName || '') !== String(programName || '')) return;
    const count =
      getFilteredSnapshotEnrollmentCount(snapshot) ??
      coerceEnrollmentCount(snapshot?.count) ??
      coerceEnrollmentCount(snapshot?.total) ??
      coerceEnrollmentCount(snapshot?.rowCount);
    if (count === null) return;
    const timestamp = getSnapshotTimestamp(snapshot);
    if (!timestamp) return;
    const dayKey = timestamp.toISOString().slice(0, 10);
    const existing = daily.get(dayKey);
    if (!existing || timestamp.getTime() > existing.timestamp.getTime()) {
      daily.set(dayKey, { timestamp, count });
    }
  });
  return Array.from(daily.values()).sort((a, b) => a.timestamp - b.timestamp);
};

const WipHomeLandingPage = () => {
  const theme = useTheme();
  const { useAllEnrollmentData, useSnapshots } = useApi();
  const { data: allEnrollmentData, isLoading, error } = useAllEnrollmentData();
  const { data: snapshots, isLoading: snapshotsLoading } = useSnapshots();
  const [timeframe, setTimeframe] = React.useState('12m');
  const [chartTab, setChartTab] = React.useState('enrollmentTrends');
  const [marketWidgetTab, setMarketWidgetTab] = React.useState('totalMarketShare');
  const [deltaWindow, setDeltaWindow] = React.useState('30d');
  const [hoveredProgram, setHoveredProgram] = React.useState(null);
  const [expandedProgram, setExpandedProgram] = React.useState(null);
  const [typedHeroTagline, setTypedHeroTagline] = React.useState('');

  React.useEffect(() => {
    setHoveredProgram(null);
    setExpandedProgram(null);
  }, [marketWidgetTab]);

  React.useEffect(() => {
    let index = 0;
    setTypedHeroTagline('');
    const timerId = window.setInterval(() => {
      index += 1;
      setTypedHeroTagline(HERO_TAGLINE.slice(0, index));
      if (index >= HERO_TAGLINE.length) {
        window.clearInterval(timerId);
      }
    }, 100);
    return () => window.clearInterval(timerId);
  }, []);

  const months = timeframe === '6m' ? 6 : timeframe === '24m' ? 24 : 12;
  const { keys: monthKeys, labels: monthLabels } = React.useMemo(() => buildMonthBuckets(months), [months]);
  const monthIndexByKey = React.useMemo(() => new Map(monthKeys.map((key, index) => [key, index])), [monthKeys]);

  const dashboardMetrics = React.useMemo(() => {
    const entries = Array.isArray(allEnrollmentData) ? allEnrollmentData : [];
    let mrr = 0;
    const enrolledBacs = new Set();
    const byProgramSets = new Map();

    entries.forEach((programEntry) => {
      const programName = String(programEntry?.programName || 'Unknown Program');
      const programRows = Array.isArray(programEntry?.data) ? programEntry.data : [];
      if (!byProgramSets.has(programName)) {
        byProgramSets.set(programName, Array.from({ length: monthKeys.length }, () => new Set()));
      }
      const monthSets = byProgramSets.get(programName);

      programRows.forEach((row) => {
        const bac = String(row?.bac || row?.BAC_Code__c || '').trim();
        if (bac) enrolledBacs.add(bac);
        const enrollmentDate = resolveEnrollmentDate(row);
        if (enrollmentDate && bac) {
          const key = `${enrollmentDate.getUTCFullYear()}-${String(enrollmentDate.getUTCMonth() + 1).padStart(2, '0')}`;
          const idx = monthIndexByKey.get(key);
          if (idx !== undefined) monthSets[idx].add(bac);
        }
        const selections = Array.isArray(row?.productSelections) ? row.productSelections : [];
        const dedupe = new Set();
        selections.forEach((selection) => {
          const selectionKey = resolveSelectionKey(selection);
          if (dedupe.has(selectionKey)) return;
          dedupe.add(selectionKey);
          mrr += getMonthlyMrr(selection);
        });
      });
    });

    const series = Array.from(byProgramSets.entries()).map(([programName, sets]) => ({
      id: programName,
      label: programName,
      data: sets.map((set) => set.size),
    }));

    const arr = mrr * 12;
    return {
      dpeMrr: mrr,
      dpeArr: arr,
      enrolledBacCount: enrolledBacs.size,
      chartSeries: series,
    };
  }, [allEnrollmentData, monthIndexByKey, monthKeys.length]);

  const chartColors = [
    theme.palette.primary.main,
    theme.palette.secondary.main,
    theme.palette.success.main,
    theme.palette.warning.main,
    theme.palette.info.main,
    theme.palette.error.main,
    alpha(theme.palette.primary.main, 0.75),
    alpha(theme.palette.secondary.main, 0.75),
    alpha(theme.palette.success.main, 0.75),
    alpha(theme.palette.warning.main, 0.75),
    alpha(theme.palette.info.main, 0.75),
  ];

  const totalEnrollmentsChart = React.useMemo(() => {
    const entries = Array.isArray(snapshots) ? snapshots : [];
    if (!entries.length) return { labels: monthLabels, series: [] };
    const cutoff = new Date();
    cutoff.setUTCMonth(cutoff.getUTCMonth() - months);

    const marketShareProgramOrder = marketShareInputs.map((item) => item.lookupNames[0]);
    const snapshotProgramNames = Array.from(
      new Set(entries.map((entry) => String(entry?.programName || '').trim()).filter(Boolean))
    );
    const orderedProgramNames = [
      ...marketShareProgramOrder,
      ...snapshotProgramNames.filter((name) => !marketShareProgramOrder.includes(name)),
    ];

    const pointsByProgram = new Map(
      orderedProgramNames.map((name) => [
        name,
        buildProgramSnapshotPoints(entries, name).filter((point) => point.timestamp >= cutoff),
      ])
    );

    const monthKeyFromDate = (date) =>
      `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}`;

    const series = orderedProgramNames.map((name) => {
      const points = pointsByProgram.get(name) || [];
      const countByMonth = new Map();
      points.forEach((point) => {
        const monthKey = monthKeyFromDate(point.timestamp);
        const existing = countByMonth.get(monthKey);
        if (!existing || point.timestamp.getTime() > existing.timestamp.getTime()) {
          countByMonth.set(monthKey, point);
        }
      });
      let carry = 0;
      return {
        id: name,
        label: name,
        data: monthKeys.map((key) => {
          const currentPoint = countByMonth.get(key);
          if (currentPoint) carry = currentPoint.count;
          return carry;
        }),
      };
    });
    return { labels: monthLabels, series };
  }, [monthKeys, monthLabels, months, snapshots]);

  const programChangeByWindow = React.useMemo(() => {
    const entries = Array.isArray(snapshots) ? snapshots : [];
    if (!entries.length) {
      return new Map(deltaWindowOptions.map((option) => [option.key, new Map()]));
    }
    const now = new Date();
    const out = new Map(deltaWindowOptions.map((option) => [option.key, new Map()]));

    marketShareInputs.forEach((item) => {
      const programName = item.lookupNames[0];
      const points =
        item.lookupNames
          .map((name) => buildProgramSnapshotPoints(entries, name))
          .find((programPoints) => programPoints.length > 0) || [];

      deltaWindowOptions.forEach((windowOption) => {
        const windowMap = out.get(windowOption.key);
        if (!points.length) {
          windowMap.set(programName, { pct: 0, bac: 0 });
          return;
        }
        const cutoff = new Date(now.getTime() - windowOption.days * 24 * 60 * 60 * 1000);
        const latest = points[points.length - 1]?.count ?? 0;
        const priorCandidates = points.filter((point) => point.timestamp <= cutoff);
        const prior = (priorCandidates.length ? priorCandidates[priorCandidates.length - 1]?.count : points[0]?.count) ?? 0;
        if (!prior) {
          windowMap.set(programName, { pct: 0, bac: 0 });
          return;
        }
        const deltaPct = ((latest - prior) / prior) * 100;
        windowMap.set(programName, { pct: deltaPct, bac: latest - prior });
      });
    });
    return out;
  }, [snapshots]);

  const marketShareCards = React.useMemo(
    () => {
      const liveCountsByProgram = new Map();
      (Array.isArray(allEnrollmentData) ? allEnrollmentData : []).forEach((programEntry) => {
        const key = String(programEntry?.programName || '').trim();
        if (!key) return;
        const rows = Array.isArray(programEntry?.data) ? programEntry.data : [];
        const bacs = new Set();
        rows.forEach((row) => {
          const bac = String(row?.bac || row?.BAC_Code__c || '').trim();
          if (!bac) return;
          if (!ACTIVE_ENROLLMENT_STATUSES.has(resolveEnrollmentStatusCode(row))) return;
          bacs.add(bac);
        });
        liveCountsByProgram.set(key.toLowerCase(), bacs.size);
      });

      const ringColors = [
        theme.palette.primary.main,
        theme.palette.secondary.main,
        theme.palette.success.main,
        theme.palette.warning.main,
        theme.palette.info.main,
        theme.palette.error.main,
      ];

      return marketShareInputs.map((entry, index) => {
        const liveEnrolledBacs =
          entry.lookupNames
            .map((name) => liveCountsByProgram.get(String(name).toLowerCase()))
            .find((value) => Number.isFinite(value)) ?? 0;
        const totalBacs = Math.round(liveEnrolledBacs / (entry.sharePct / 100));
        const currentSharePct = totalBacs > 0 ? (liveEnrolledBacs / totalBacs) * 100 : 0;
        const shareDelta = currentSharePct - entry.sharePct;
        const deltaStats = programChangeByWindow.get(deltaWindow)?.get(entry.lookupNames[0]) || { pct: 0, bac: 0 };
        return {
          ...entry,
          totalBacs,
          liveEnrolledBacs,
          currentSharePct,
          shareDelta,
          deltaPct: deltaStats.pct,
          deltaBac: deltaStats.bac,
          ringColor: ringColors[index % ringColors.length],
        };
      });
    },
    [
      allEnrollmentData,
      deltaWindow,
      programChangeByWindow,
      theme.palette.error.main,
      theme.palette.info.main,
      theme.palette.primary.main,
      theme.palette.secondary.main,
      theme.palette.success.main,
      theme.palette.warning.main,
    ]
  );

  return (
    <Box
      sx={{
        bgcolor: 'background.default',
        borderRadius: 3,
        minHeight: 'calc(100vh - 120px)',
        px: { xs: 1.5, md: 3 },
        py: { xs: 2, md: 3.5 },
      }}
    >
      <Box sx={{ maxWidth: 1280, mx: 'auto' }}>
        <Card
          variant="outlined"
          sx={{
            borderColor: 'divider',
            bgcolor: 'background.paper',
            boxShadow: `0 18px 45px ${alpha(theme.palette.common.black, theme.palette.mode === 'dark' ? 0.3 : 0.08)}`,
            overflow: 'hidden',
            backgroundImage: `radial-gradient(500px 180px at 12% -10%, ${alpha(theme.palette.primary.main, 0.18)}, transparent 72%), radial-gradient(520px 190px at 92% 120%, ${alpha(theme.palette.success.main, 0.14)}, transparent 74%)`,
          }}
        >
          <CardContent sx={{ p: { xs: 2, md: 4 } }}>
            <Stack
              direction={{ xs: 'column', lg: 'row' }}
              spacing={{ xs: 2, lg: 4 }}
              justifyContent="flex-start"
              alignItems={{ xs: 'flex-start', lg: 'flex-start' }}
            >
              <Box sx={{ maxWidth: 820 }}>
                <Typography
                  component="div"
                  sx={{
                    fontFamily: '"Avenir Next", "Segoe UI", "Trebuchet MS", sans-serif',
                    color: 'text.primary',
                    fontWeight: 750,
                    fontSize: { xs: 36, md: 58 },
                    lineHeight: 0.95,
                    letterSpacing: '-0.03em',
                    whiteSpace: { xs: 'normal', md: 'nowrap' },
                  }}
                >
                  <Box component="span">GMER</Box>
                  <Box
                    component="span"
                    sx={{
                      display: 'inline-block',
                      opacity: 0,
                      transform: 'translate(-72px, -46px) scale(0.08) rotate(-84deg)',
                      animation: 'gmerDotFlyIn 1150ms cubic-bezier(0.2, 0.9, 0.24, 1.2) 320ms forwards',
                      '@keyframes gmerDotFlyIn': {
                        '0%': {
                          opacity: 0,
                          transform: 'translate(-72px, -46px) scale(0.08) rotate(-84deg)',
                        },
                        '50%': {
                          opacity: 1,
                          transform: 'translate(16px, 11px) scale(1.75) rotate(22deg)',
                        },
                        '78%': {
                          opacity: 1,
                          transform: 'translate(-8px, -5px) scale(0.82) rotate(-10deg)',
                        },
                        '100%': {
                          opacity: 1,
                          transform: 'translate(0, 0) scale(1) rotate(0deg)',
                        },
                      },
                    }}
                  >
                    .
                  </Box>
                </Typography>
                <Box sx={{ mt: 2.2, maxWidth: 420 }}>
                  <Box
                    sx={{
                      height: 4,
                      borderRadius: 999,
                      background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${alpha(theme.palette.primary.main, 0.25)})`,
                    }}
                  />
                  <Typography
                    sx={{ mt: 1, color: 'text.secondary', fontSize: { xs: 16, md: 20 }, letterSpacing: 0.2, whiteSpace: 'nowrap' }}
                  >
                    {typedHeroTagline}
                  </Typography>
                </Box>
              </Box>
            </Stack>
          </CardContent>
        </Card>

        {error ? (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error?.message || 'Failed to load dashboard metrics.'}
          </Alert>
        ) : null}

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.2} sx={{ mt: 2 }}>
          <Card variant="outlined" sx={{ flex: 1, borderColor: 'divider' }}>
            <CardContent sx={{ position: 'relative' }}>
              <Tooltip title="Does not include DigAd sales or service toward value">
                <IconButton size="small" sx={{ position: 'absolute', top: 10, right: 10, color: 'primary.main' }}>
                  <InfoOutlinedIcon fontSize="inherit" />
                </IconButton>
              </Tooltip>
              <Stack direction="row" alignItems="center" spacing={1}>
                <PaymentsOutlinedIcon color="primary" fontSize="small" />
                <Typography variant="overline" color="text.secondary">
                  DPE MRR
                </Typography>
              </Stack>
              <Typography variant="h4" sx={{ fontWeight: 800, mt: 0.2 }}>
                {currency.format(dashboardMetrics.dpeMrr)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Estimated monthly recurring revenue from product selections
              </Typography>
            </CardContent>
          </Card>
          <Card variant="outlined" sx={{ flex: 1, borderColor: 'divider' }}>
            <CardContent sx={{ position: 'relative' }}>
              <Tooltip title="Does not include DigAd sales or service toward value">
                <IconButton size="small" sx={{ position: 'absolute', top: 10, right: 10, color: 'primary.main' }}>
                  <InfoOutlinedIcon fontSize="inherit" />
                </IconButton>
              </Tooltip>
              <Stack direction="row" alignItems="center" spacing={1}>
                <CalendarMonthOutlinedIcon color="primary" fontSize="small" />
                <Typography variant="overline" color="text.secondary">
                  DPE ARR
                </Typography>
              </Stack>
              <Typography variant="h4" sx={{ fontWeight: 800, mt: 0.2 }}>
                {currency.format(dashboardMetrics.dpeArr)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Annualized recurring revenue from current MRR projection
              </Typography>
            </CardContent>
          </Card>
          <Card variant="outlined" sx={{ flex: 1, borderColor: 'divider' }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1}>
                <ShowChartOutlinedIcon color="primary" fontSize="small" />
                <Typography variant="overline" color="text.secondary">
                  Active BACs
                </Typography>
              </Stack>
              <Typography variant="h4" sx={{ fontWeight: 800, mt: 0.2 }}>
                {dashboardMetrics.enrolledBacCount.toLocaleString()}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Unique BACs represented across all loaded programs
              </Typography>
            </CardContent>
          </Card>
        </Stack>

        <Card variant="outlined" sx={{ mt: 2, borderColor: 'divider' }}>
          <CardContent sx={{ p: { xs: 1.6, md: 2.2 } }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={1.2}>
              <Box sx={{ width: '100%' }}>
                <Tabs
                  value={chartTab}
                  onChange={(_event, value) => setChartTab(value)}
                  sx={{ minHeight: 36, '& .MuiTab-root': { minHeight: 36, textTransform: 'none', fontWeight: 700 } }}
                >
                  <Tab label="Enrollment Trends" value="enrollmentTrends" />
                  <Tab label="Total Enrollments" value="totalEnrollments" />
                </Tabs>
                <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.4 }}>
                  {chartTab === 'enrollmentTrends'
                    ? 'Multi-line view of unique BAC enrollments over time.'
                    : 'Historical snapshot totals by program, excluding opted-out and cancelled BACs.'}
                </Typography>
              </Box>
              <FormControl size="small">
                <Select value={timeframe} onChange={(event) => setTimeframe(event.target.value)}>
                  <MenuItem value="6m">Last 6 months</MenuItem>
                  <MenuItem value="12m">Last 12 months</MenuItem>
                  <MenuItem value="24m">Last 24 months</MenuItem>
                </Select>
              </FormControl>
            </Stack>
            <Divider sx={{ my: 1.2 }} />
            {isLoading || (chartTab === 'totalEnrollments' && snapshotsLoading) ? (
              <Box sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress size={28} />
              </Box>
            ) : (
              <Box sx={{ width: '100%', overflowX: 'auto' }}>
                <Box sx={{ minWidth: 720 }}>
                  {chartTab === 'enrollmentTrends' ? (
                    <LineChart
                      height={360}
                      xAxis={[{ data: monthLabels, scaleType: 'point' }]}
                      series={dashboardMetrics.chartSeries.map((entry, index) => ({
                        id: entry.id,
                        label: entry.label,
                        data: entry.data,
                        curve: 'monotoneX',
                        showMark: false,
                        color: chartColors[index % chartColors.length],
                      }))}
                      margin={{ top: 20, right: 24, bottom: 24, left: 44 }}
                      slotProps={{ legend: { hidden: false } }}
                      grid={{ vertical: false, horizontal: true }}
                    />
                  ) : (
                    <LineChart
                      height={360}
                      xAxis={[
                        {
                          data: totalEnrollmentsChart.labels,
                          scaleType: 'point',
                        },
                      ]}
                      series={totalEnrollmentsChart.series.map((entry, index) => ({
                        id: entry.id,
                        label: entry.label,
                        data: entry.data,
                        curve: 'monotoneX',
                        showMark: false,
                        color: chartColors[index % chartColors.length],
                      }))}
                      margin={{ top: 20, right: 24, bottom: 24, left: 44 }}
                      slotProps={{ legend: { hidden: false } }}
                      grid={{ vertical: false, horizontal: true }}
                    />
                  )}
                </Box>
              </Box>
            )}
          </CardContent>
        </Card>

        <Card variant="outlined" sx={{ mt: 2, borderColor: 'divider' }}>
          <CardContent sx={{ p: { xs: 1.6, md: 2.2 } }}>
            <Tabs
              value={marketWidgetTab}
              onChange={(_event, value) => setMarketWidgetTab(value)}
              sx={{ minHeight: 36, '& .MuiTab-root': { minHeight: 36, textTransform: 'none', fontWeight: 700 } }}
            >
              <Tab label="Total Market Share" value="totalMarketShare" />
              <Tab label="Enrollment Change" value="enrollmentChange" />
            </Tabs>
            <Stack direction="row" spacing={0.8} sx={{ mt: 0.8, mb: 1.1 }}>
              {deltaWindowOptions.map((option) => (
                <Chip
                  key={option.key}
                  size="small"
                  label={option.label}
                  clickable
                  onClick={() => setDeltaWindow(option.key)}
                  color={deltaWindow === option.key ? 'primary' : 'default'}
                  variant={deltaWindow === option.key ? 'filled' : 'outlined'}
                />
              ))}
            </Stack>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.2 }}>
              {marketWidgetTab === 'totalMarketShare'
                ? 'Implied total BAC universe per program, based on enrolled BAC counts and your target market-share values.'
                : 'Current enrolled BACs per program with percent change over the selected timeframe.'}
            </Typography>
            <Divider sx={{ mb: 1.6 }} />
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: 'repeat(1, minmax(0, 1fr))', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(3, minmax(0, 1fr))' },
                gap: 1.2,
              }}
            >
              {marketShareCards.map((card) => {
                const detailsVisible = hoveredProgram === card.program || expandedProgram === card.program;
                if (marketWidgetTab === 'totalMarketShare') {
                  return (
                    <Card
                      key={card.program}
                      variant="outlined"
                      onMouseEnter={() => setHoveredProgram(card.program)}
                      onMouseLeave={() => setHoveredProgram(null)}
                      onClick={() => setExpandedProgram((previous) => (previous === card.program ? null : card.program))}
                      sx={{
                        borderColor: 'divider',
                        cursor: 'pointer',
                        transition: 'border-color 140ms ease, box-shadow 140ms ease',
                        '&:hover': {
                          borderColor: alpha(card.ringColor, 0.6),
                          boxShadow: `0 10px 22px ${alpha(card.ringColor, 0.15)}`,
                        },
                      }}
                    >
                      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ position: 'relative', width: 92, height: 92, flexShrink: 0 }}>
                          <CircularProgress
                            variant="determinate"
                            value={100}
                            size={92}
                            thickness={5}
                            sx={{ color: alpha(theme.palette.text.secondary, 0.18), position: 'absolute', inset: 0 }}
                          />
                          <CircularProgress
                            variant="determinate"
                            value={card.sharePct}
                            size={92}
                            thickness={5}
                            sx={{ color: card.ringColor, position: 'absolute', inset: 0 }}
                          />
                          <Box
                            sx={{
                              position: 'absolute',
                              inset: 0,
                              display: 'grid',
                              placeItems: 'center',
                            }}
                          >
                            <Typography sx={{ fontWeight: 800, fontSize: 18 }}>{card.sharePct}%</Typography>
                          </Box>
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            {card.program}
                          </Typography>
                          {detailsVisible ? (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.4 }}>
                              {integer.format(card.liveEnrolledBacs)} / {integer.format(card.totalBacs)} BACs
                            </Typography>
                          ) : (
                            <Chip
                              size="small"
                              sx={{ mt: 0.5 }}
                              label={`${card.deltaPct >= 0 ? '+' : ''}${card.deltaPct.toFixed(1)}%`}
                              color={card.deltaPct < 0 ? 'error' : 'success'}
                              variant="outlined"
                            />
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  );
                }

                return (
                  <Card
                    key={card.program}
                    variant="outlined"
                    onMouseEnter={() => setHoveredProgram(card.program)}
                    onMouseLeave={() => setHoveredProgram(null)}
                    onClick={() => setExpandedProgram((previous) => (previous === card.program ? null : card.program))}
                    sx={{
                      borderColor: 'divider',
                      cursor: 'pointer',
                      transition: 'border-color 140ms ease, box-shadow 140ms ease',
                      '&:hover': {
                        borderColor: alpha(card.ringColor, 0.6),
                        boxShadow: `0 10px 22px ${alpha(card.ringColor, 0.15)}`,
                      },
                    }}
                  >
                    <CardContent>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        {card.program}
                      </Typography>
                      <Typography sx={{ mt: 0.5, fontSize: 32, lineHeight: 1.05, fontWeight: 800 }}>
                        {integer.format(card.liveEnrolledBacs)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Current BAC enrolled
                      </Typography>
                      <Box sx={{ mt: 1 }}>
                        {detailsVisible ? (
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 700,
                              color: card.deltaBac < 0 ? 'error.main' : 'success.main',
                            }}
                          >
                            {card.deltaBac >= 0 ? '+' : ''}
                            {integer.format(card.deltaBac)} BACs
                          </Typography>
                        ) : (
                          <Chip
                            size="small"
                            label={`${card.deltaPct >= 0 ? '+' : ''}${card.deltaPct.toFixed(1)}%`}
                            color={card.deltaPct < 0 ? 'error' : 'success'}
                            variant="outlined"
                          />
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                );
              })}
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default WipHomeLandingPage;
